// src/pages/contact/ContactPage.js
import React, { useEffect, useState } from "react";
import { sendContactMessage, getMyContacts } from "../../api/contactApi";
import TextInput from "../../components/common/TextInput";
import Button from "../../components/common/Button";
import { useAuth } from "../../hooks/useAuth";
import {
  FiMail,
  FiMessageSquare,
  FiHeadphones,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiSend,
  FiPhone,
  FiMapPin,
  FiInfo,
  FiDollarSign,
  FiTool
} from "react-icons/fi";

const CONTACT_TYPES = [
  {
    value: "support",
    label: "General Support",
    icon: FiHeadphones,
    color: "blue",
    gradient: "from-blue-500 to-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    iconBg: "bg-blue-100",
    description: "Get help with your account or services"
  },
  {
    value: "payment",
    label: "Payment Issue",
    icon: FiDollarSign,
    color: "green",
    gradient: "from-green-500 to-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
    iconBg: "bg-green-100",
    description: "Questions about payments or withdrawals"
  },
  {
    value: "technical",
    label: "Technical Issue",
    icon: FiTool,
    color: "orange",
    gradient: "from-orange-500 to-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
    iconBg: "bg-orange-100",
    description: "Report bugs or technical problems"
  },
  {
    value: "other",
    label: "Other Inquiry",
    icon: FiInfo,
    color: "purple",
    gradient: "from-purple-500 to-purple-600",
    bg: "bg-purple-50",
    border: "border-purple-200",
    text: "text-purple-700",
    iconBg: "bg-purple-100",
    description: "Any other questions or feedback"
  }
];

function ContactPage() {
  const [type, setType] = useState("support");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);

  const { user } = useAuth();
  const userId = user.user_id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!message.trim() && !subject.trim()) {
      setError("Please enter a message or subject.");
      return;
    }

    try {
      setLoading(true);

      const finalMessage = subject
        ? `Subject: ${subject}\n\n${message}`
        : message;
      const contactData = {
        type,
        subject,
        message: finalMessage,
      };

      const data = await sendContactMessage(userId, contactData);

      setSuccess(data.message || "Message sent successfully! We'll get back to you soon.");
      setSubject("");
      setMessage("");

      await loadContacts();
    } catch (err) {
      console.error(err);
      setError("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      const data = await getMyContacts(userId);
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const selectedType = CONTACT_TYPES.find(t => t.value === type) || CONTACT_TYPES[0];

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Hero Header Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 p-10 shadow-xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
        <div className="relative flex items-center gap-6">
          <div className="flex-shrink-0">
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center">
              <FiHeadphones className="h-10 w-10 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-white mb-2">Contact Support</h1>
            <p className="text-xl text-primary-100">
              Have questions? We're here to help 24/7. Send us a message and we'll respond quickly.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border-2 border-blue-100 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <FiMail className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-600">Email</p>
              <p className="text-sm font-bold text-gray-900 truncate">support@reviewmarket.eu</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-green-100 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <FiPhone className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-600">Phone</p>
              <p className="text-sm font-bold text-gray-900">+1 (555) 123-4567</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-orange-100 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <FiMapPin className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-600">Location</p>
              <p className="text-sm font-bold text-gray-900">Europe, EU</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-purple-100 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <FiClock className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-600">Response Time</p>
              <p className="text-sm font-bold text-gray-900">Within 24 hours</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Contact Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Alert Messages */}
          {error && (
            <div className="rounded-xl border-2 border-red-200 bg-red-50 px-5 py-4 flex items-start gap-3">
              <FiAlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}
          {success && (
            <div className="rounded-xl border-2 border-green-200 bg-green-50 px-5 py-4 flex items-start gap-3">
              <FiCheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-green-700">{success}</p>
            </div>
          )}

          {/* Contact Form Card */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200 px-8 py-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${selectedType.gradient} flex items-center justify-center`}>
                  <FiSend className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Send Message</h2>
                  <p className="text-sm text-gray-600">Fill out the form below to contact our support team</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Type Selection - Professional Cards */}
              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-900">
                  What can we help you with? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {CONTACT_TYPES.map((contactType) => {
                    const Icon = contactType.icon;
                    const isSelected = type === contactType.value;
                    return (
                      <button
                        key={contactType.value}
                        type="button"
                        onClick={() => setType(contactType.value)}
                        className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                          isSelected
                            ? `${contactType.border} ${contactType.bg} shadow-md`
                            : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${isSelected ? `bg-gradient-to-br ${contactType.gradient}` : contactType.iconBg} flex items-center justify-center`}>
                            <Icon className={`h-6 w-6 ${isSelected ? 'text-white' : contactType.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-bold mb-1 ${isSelected ? contactType.text : 'text-gray-900'}`}>
                              {contactType.label}
                            </div>
                            <div className="text-xs text-gray-600">
                              {contactType.description}
                            </div>
                          </div>
                          {isSelected && (
                            <FiCheckCircle className={`h-5 w-5 flex-shrink-0 ${contactType.text}`} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Subject Input */}
              <TextInput
                label="Subject (Optional)"
                name="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your inquiry"
              />

              {/* Message Textarea */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-900">
                  Your Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Please describe your issue or question in detail..."
                  className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 bg-white resize-y transition-all placeholder:text-gray-400"
                  required
                />
                <p className="text-xs text-gray-500">
                  Provide as much detail as possible to help us assist you effectively.
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex items-center gap-4 pt-2">
                <Button
                  type="submit"
                  loading={loading}
                  size="lg"
                  className="flex-1 md:flex-initial"
                >
                  <FiSend className="h-5 w-5 mr-2" />
                  Send Message
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar - Quick Help */}
        <div className="space-y-6">
          {/* Quick Tips Card */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-b-2 border-purple-200 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <FiInfo className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Quick Tips</h3>
              </div>
            </div>
            <div className="p-6">
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <FiCheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Include your user ID for faster support</span>
                </li>
                <li className="flex items-start gap-3">
                  <FiCheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Attach screenshots if reporting issues</span>
                </li>
                <li className="flex items-start gap-3">
                  <FiCheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Check FAQ before contacting support</span>
                </li>
                <li className="flex items-start gap-3">
                  <FiCheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Average response time: 24 hours</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Support Hours Card */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <FiClock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Support Hours</h3>
                <p className="text-sm text-amber-800">
                  Our support team is available 24/7 to assist you with any questions or concerns you may have.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message History */}
      <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200 px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <FiMessageSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Message History</h2>
              <p className="text-sm text-gray-600">Track all your conversations with our support team</p>
            </div>
          </div>
        </div>

        <div className="p-8">
          {loadingContacts ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full mb-4" style={{ animation: 'spin 1s linear infinite' }}></div>
              <p className="text-gray-600 font-medium">Loading messages...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FiMessageSquare className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No Messages Yet</h3>
              <p className="text-gray-600">
                When you contact us, your message history will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {contacts.map((contact) => {
                const contactType = CONTACT_TYPES.find(t => t.value === contact.type) || CONTACT_TYPES[0];
                const TypeIcon = contactType.icon;
                const isResolved = contact.status === "RESOLVED";
                const isOpen = contact.status === "OPEN";

                return (
                  <div
                    key={contact.contact_id}
                    className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* Message Header */}
                    <div className={`${contactType.bg} border-b-2 ${contactType.border} px-6 py-4`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${contactType.gradient} flex items-center justify-center`}>
                            <TypeIcon className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`font-bold ${contactType.text}`}>
                                {contactType.label}
                              </span>
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold border-2 ${
                                  isResolved
                                    ? "bg-green-50 text-green-700 border-green-200"
                                    : isOpen
                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : "bg-gray-50 text-gray-700 border-gray-200"
                                }`}
                              >
                                {isResolved ? (
                                  <FiCheckCircle className="h-3.5 w-3.5" />
                                ) : (
                                  <FiClock className="h-3.5 w-3.5" />
                                )}
                                {isResolved ? "Replied" : isOpen ? "Unread" : "Pending"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <FiClock className="h-3.5 w-3.5" />
                              {formatDateTime(contact.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Message Content */}
                    <div className="p-6">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-5 border-2 border-gray-200">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed font-medium">
                          {contact.message}
                        </p>
                      </div>

                      {/* Admin Reply */}
                      {contact.reply && (
                        <div className="mt-6 relative pl-6">
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full"></div>
                          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-5 border-2 border-primary-200">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center">
                                <FiHeadphones className="h-4 w-4 text-white" />
                              </div>
                              <span className="text-xs font-bold text-primary-900 uppercase tracking-wide">
                                Support Team Reply
                              </span>
                            </div>
                            <p className="text-sm text-primary-900 whitespace-pre-wrap leading-relaxed font-medium">
                              {contact.reply}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

export default ContactPage;
